<script setup lang="ts">
/**
 *
 *  Props and emits section
 *
 *
 */
const { get: getBanners, data: banners } = useOFetchCustom(
    "banners/getAllActives"
)

const indexBanner = ref(0)

const currentBanner = computed(() => banners?.value?.data?.[indexBanner.value])
const isCurrentFullWidth = computed(
    () => currentBanner.value?.layout === "full_width"
)

let intervalBanners: ReturnType<typeof setInterval> | undefined

const props = defineProps<{
    title: string
    subtitle: string
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

/**
 *
 *  Life cicle events
 *
 *
 */

onMounted(() => {
    getBanners()
    intervalBanners = setInterval(() => {
        if (banners?.value?.data?.length > 0) {
            indexBanner.value =
                (indexBanner.value + 1) % banners?.value?.data?.length
            if (indexBanner.value >= banners?.value?.data?.length) {
                indexBanner.value = 0
            }
        }
    }, 8000)
})

onBeforeUnmount(() => {
    clearInterval(intervalBanners!)
})

/**
 *
 *  Functions section
 *
 *
 */
</script>

<template>
    <div class="tw-relative tw-h-max">
        <!-- Desktop Full-width banner -->
        <component
            :is="currentBanner?.url_redirect ? 'a' : 'div'"
            v-if="isCurrentFullWidth"
            :href="currentBanner?.url_redirect || undefined"
            :target="currentBanner?.url_redirect ? '_blank' : undefined"
            rel="noopener noreferrer"
            class="tw-hidden md:tw-block tw-relative tw-w-full tw-aspect-[31/9] tw-overflow-hidden"
            :class="{ 'tw-cursor-pointer': currentBanner?.url_redirect }"
        >
            <img
                :src="currentBanner?.image"
                :alt="currentBanner?.title || 'Banner'"
                class="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover"
            />
        </component>

        <!-- Desktop Split banner -->
        <template v-else>
            <div
                class="tw-hidden md:tw-block tw-w-full home-carousel-section-container tw-relative lg:tw-grid lg:tw-grid-cols-2"
                :class="{ 'tw-bg-gray-300': !banners?.data?.length }"
                :style="
                    banners?.data?.length
                        ? {
                              backgroundColor:
                                  banners.data[indexBanner]?.background_color ||
                                  '#ff0000',
                          }
                        : {}
                "
            >
                <div
                    class="tw-hidden md:tw-flex md:tw-flex-col tw-justify-center tw-items-center tw-overflow-hidden !tw-h-max tw-py-5"
                >
                    <div
                        class="lg:tw-hidden tw-text-white tw-font-bold tw-text-[60px] md:tw-text-5xl lg:tw-text-[72px] max-container tw-gap-8 tw-flex tw-flex-col tw-justify-center"
                    >
                        <div
                            class="tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-4"
                        >
                            <p>
                                {{
                                    banners?.data.length > 0
                                        ? banners?.data[indexBanner]?.title
                                        : props.title
                                }}
                            </p>
                            <span class="translateUp-normal tw-text-2xl">
                                {{
                                    banners?.data.length > 0
                                        ? banners?.data[indexBanner]?.subtitle
                                        : props.subtitle
                                }}
                            </span>
                        </div>
                        <NuxtLink
                            :style="
                                banners?.data?.length
                                    ? {
                                          backgroundColor:
                                              banners.data[indexBanner]
                                                  ?.color_button || '#004482',
                                      }
                                    : {}
                            "
                            class="tw-bg-primary tw-text-white tw-font-bold tw-text-sm tw-rounded-full tw-py-2 tw-px-10 tw-mx-auto tw-mb-6"
                            :to="
                                banners?.data[indexBanner]?.url_redirect
                                    ? banners?.data[indexBanner]?.url_redirect
                                    : '/store?onlyCombo=true'
                            "
                        >
                            {{
                                banners?.data[indexBanner]?.title_button
                                    ? banners?.data[indexBanner]?.title_button
                                    : "Combos"
                            }}
                        </NuxtLink>
                    </div>
                </div>
                <CustomCarousel
                    :bannersImage="banners?.data[indexBanner]"
                    class="tw-z-20 tw-h-full lg:tw-col-start-2"
                />
            </div>
            <div
                class="tw-hidden tw-text-white tw-font-bold tw-text-[60px] md:tw-text-5xl lg:tw-text-[72px] tw-absolute max-container tw-gap-8 tw-inset-0 lg:tw-flex tw-flex-col tw-justify-center"
            >
                <div
                    class="tw-flex tw-flex-col tw-justify-center tw-gap-4 tw-w-1/2 tw-pr-12"
                >
                    <h2 class="translateUp-normal tw-leading-tight">
                        {{
                            banners?.data.length > 0
                                ? banners?.data[indexBanner]?.title
                                : props.title
                        }}
                    </h2>
                    <span
                        class="translateUp-normal tw-text-2xl lg:tw-text-[36px]"
                    >
                        {{
                            banners?.data.length > 0
                                ? banners?.data[indexBanner]?.subtitle
                                : props.subtitle
                        }}
                    </span>
                </div>
                <NuxtLink
                    class="tw-bg-primary tw-text-white tw-font-bold tw-text-sm tw-rounded-full tw-py-2 tw-px-10 tw-mr-auto"
                    :style="
                        banners?.data?.length
                            ? {
                                  backgroundColor:
                                      banners.data[indexBanner]?.color_button ||
                                      '#004482',
                              }
                            : {}
                    "
                    :to="
                        banners?.data[indexBanner]?.url_redirect
                            ? banners?.data[indexBanner]?.url_redirect
                            : '/store?onlyCombo=true'
                    "
                >
                    {{
                        banners?.data[indexBanner]?.title_button
                            ? banners?.data[indexBanner]?.title_button
                            : "Combos"
                    }}
                </NuxtLink>
            </div>
        </template>

        <!-- Mobile Carousel -->
        <div class="md:tw-hidden tw-relative tw-w-full tw-mt-4">
            <div v-if="banners?.data?.length" class="max-container">
                <!-- Horizontal Scroll Container -->
                <div
                    class="tw-flex tw-gap-4 tw-overflow-x-auto tw-pb-4 tw-scrollbar-hide tw-snap-x tw-snap-mandatory"
                >
                    <div
                        v-for="(banner, index) in banners.data"
                        :key="index"
                        class="tw-relative tw-rounded-[16px] tw-overflow-hidden tw-flex-shrink-0 tw-snap-center"
                        :class="
                            banner?.layout === 'full_width'
                                ? 'tw-w-full tw-aspect-square'
                                : 'tw-w-[290px] tw-h-[350px]'
                        "
                    >
                        <!-- Banner Image -->
                        <img
                            v-if="banner?.mobile_image"
                            :src="banner.mobile_image"
                            :alt="banner?.title || 'Banner'"
                            class="tw-w-full tw-h-full tw-object-cover"
                        />
                        <img
                            v-else-if="banner?.image"
                            :src="banner.image"
                            :alt="banner?.title || 'Banner'"
                            class="tw-w-full tw-h-full tw-object-cover"
                        />

                        <!-- Full-width click-through -->
                        <a
                            v-if="
                                banner?.layout === 'full_width' &&
                                banner?.url_redirect
                            "
                            :href="banner.url_redirect"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="tw-absolute tw-inset-0 tw-cursor-pointer"
                        />

                        <!-- Gradient Overlay -->
                        <div
                            v-if="banner?.layout !== 'full_width'"
                            class="tw-absolute tw-bottom-0 -tw-left-1 -tw-right-1 tw-h-[150px] tw-bg-gradient-to-t tw-from-white tw-to-transparent tw-pointer-events-none z-50"
                        ></div>

                        <div
                            v-if="banner?.layout !== 'full_width'"
                            class="tw-absolute tw-left-5 tw-bottom-16 tw-flex tw-flex-col tw-gap-2"
                        >
                            <!-- Title -->
                            <h2
                                class="tw-text-white tw-font-bold tw-text-xl tw-leading-tight tw-max-w-[250px]"
                            >
                                {{ banner?.title || "Mejores precios" }}
                            </h2>

                            <!-- Subtitle -->
                            <p
                                class="tw-text-white tw-text-xs tw-opacity-90 tw-max-w-[250px]"
                            >
                                {{
                                    banner?.subtitle ||
                                    "en todos nuestros productos"
                                }}
                            </p>
                        </div>

                        <!-- Button -->
                        <NuxtLink
                            v-if="banner?.layout !== 'full_width'"
                            :to="
                                banner?.url_redirect || '/store?onlyCombo=true'
                            "
                            :style="{
                                backgroundColor:
                                    banner?.color_button || '#FF6B35',
                            }"
                            class="tw-absolute tw-left-5 tw-bottom-0 tw-inline-block tw-text-white tw-font-bold tw-text-xs tw-rounded-full tw-py-2.5 tw-px-6 tw-text-center"
                        >
                            {{ banner?.title_button || "Ver ofertas" }}
                        </NuxtLink>
                    </div>
                </div>
            </div>

            <!-- Skeleton Loading when no banners -->
            <div v-else class="max-container">
                <div class="tw-flex tw-gap-4 tw-overflow-x-auto tw-pb-4">
                    <QSkeleton
                        v-for="n in 3"
                        :key="n"
                        class="tw-w-[290px] tw-h-[350px] tw-rounded-[16px] tw-flex-shrink-0"
                        animation="wave"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.home-carousel-section-container::after {
    content: "";
    background: url("/img/banner-img.webp") no-repeat;
    @apply tw-w-1/2 tw-max-w-[430px] tw-absolute tw-left-0 tw-bottom-1/2 lg:tw-bottom-0 tw-aspect-square tw-opacity-50;
}

/* Hide scrollbar but keep functionality */
.tw-scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.tw-scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
